

import * as React from "react";
import { ImageOff } from "lucide-react";
import { cn } from "@repo/ui/lib/utils";

export interface ImageProps
  extends Omit<
    React.ImgHTMLAttributes<HTMLImageElement>,
    "src" | "alt" | "width" | "height" | "loading" | "srcSet" | "sizes"
  > {
  src: string | StaticImageData;
  alt: string;
  width?: number;
  height?: number;
  fill?: boolean;
  priority?: boolean;
  loading?: "lazy" | "eager";
  placeholder?: "blur" | "empty";
  blurDataURL?: string;
  quality?: number;
  sizes?: string;
  objectFit?: "contain" | "cover" | "fill" | "none" | "scale-down";
  objectPosition?: string;
  onLoad?: (event: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  onError?: (event: React.SyntheticEvent<HTMLImageElement, Event>) => void;
}

export interface StaticImageData {
  src: string;
  height: number;
  width: number;
  blurDataURL?: string;
}

/**
 * Image component compatible with Next.js Image API
 * Uses native img element with web standard optimizations
 */
const Image = React.forwardRef<HTMLImageElement, ImageProps>(
  (
    {
      src,
      alt,
      width,
      height,
      fill = false,
      priority = false,
      loading,
      placeholder = "empty",
      blurDataURL,
      quality,
      sizes,
      objectFit,
      objectPosition,
      className,
      style,
      onLoad,
      onError,
      ...props
    },
    ref
  ) => {
    const [isLoaded, setIsLoaded] = React.useState(priority);
    const [hasError, setHasError] = React.useState(false);
    const [imageSrc, setImageSrc] = React.useState<string>("");

    // Resolve src from StaticImageData or string
    React.useEffect(() => {
      if (typeof src === "string") {
        setImageSrc(src);
      } else {
        setImageSrc(src.src);
      }
    }, [src]);

    // Determine loading behavior
    const loadingBehavior = React.useMemo(() => {
      if (loading) return loading;
      if (priority) return "eager";
      return "lazy";
    }, [loading, priority]);

    // Handle image load
    const handleLoad = React.useCallback(
      (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
        setIsLoaded(true);
        onLoad?.(event);
      },
      [onLoad]
    );

    // Handle image error
    const handleError = React.useCallback(
      (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
        setHasError(true);
        onError?.(event);
      },
      [onError]
    );

    // Calculate dimensions
    const imageWidth = typeof src === "object" ? src.width : width;
    const imageHeight = typeof src === "object" ? src.height : height;

    // Build style object
    const imageStyle: React.CSSProperties = {
      ...style,
      ...(fill && {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
      }),
      ...(objectFit && {
        objectFit,
      }),
      ...(objectPosition && {
        objectPosition,
      }),
    };

    // Blur placeholder
    const showBlurPlaceholder =
      placeholder === "blur" &&
      blurDataURL &&
      !isLoaded &&
      !hasError;

    // Build wrapper style
    //@ts-ignore
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const wrapperStyle: any = fill
      ? undefined
      : {
        width: imageWidth ? `${imageWidth}px` : undefined,
        height: imageHeight ? `${imageHeight}px` : undefined,
      };

    return (
      <div
        className={cn(
          "relative",
          fill ? "absolute inset-0 w-full h-full" : "inline-block"
        )}
        style={wrapperStyle}
      >
        {/* Blur placeholder */}
        {showBlurPlaceholder && (
          <img
            src={blurDataURL}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              filter: "blur(20px)",
              transform: "scale(1.1)",
            }}
          />
        )}

        {/* Main image */}
        <img
          ref={ref}
          src={imageSrc}
          alt={alt}
          width={fill ? undefined : imageWidth}
          height={fill ? undefined : imageHeight}
          loading={loadingBehavior}
          sizes={sizes}
          className={cn(
            "transition-opacity duration-300",
            fill && "absolute inset-0 w-full h-full",
            !isLoaded && "opacity-0",
            isLoaded && "opacity-100",
            hasError && "opacity-50",
            className
          )}
          style={imageStyle}
          onLoad={handleLoad}
          onError={handleError}
          {...props}
        />

        {/* Error fallback */}
        {hasError && (
          <div
            className="absolute inset-0 flex items-center justify-center bg-muted text-muted-foreground"
            aria-label={alt}
          >
            <ImageOff className="w-8 h-8 opacity-50" />
          </div>
        )}
      </div>
    );
  }
);

Image.displayName = "Image";

export default Image;
